<template>
  <div class="odt-root">

    <div class="odt-controls-bar">
      <div class="odt-controls-left">
        <div class="odt-search-wrap">
          <v-icon class="odt-search-icon" size="16" color="#9aa6b8">mdi-magnify</v-icon>
          <input v-model="search" class="odt-search" placeholder="Buscar máquina, ODT, molde…" />
          <button v-if="search" class="odt-search-clear" @click="search = ''">✕</button>
        </div>
        <select v-model="filtroActivo" class="odt-select">
          <option value="todos">Todas las máquinas</option>
          <option value="enTiempo">En tiempo</option>
          <option value="atrasadas">Atrasadas</option>
          <option value="enParo">En paro</option>
        </select>
      </div>
      <div class="odt-controls-right">
        <div class="odt-refresh-timer" :class="{ 'odt-refresh-timer--soon': timerSegundos <= 30 }">
          <svg viewBox="0 0 36 36" class="odt-timer-circle">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#dde6f0" stroke-width="2.5"/>
            <circle cx="18" cy="18" r="15" fill="none"
              :stroke="timerSegundos <= 30 ? '#f08b00' : '#2e8aed'"
              stroke-width="2.5" stroke-linecap="round"
              :stroke-dasharray="`${(timerSegundos / 300) * 94.2} 94.2`"
              transform="rotate(-90 18 18)"
              style="transition: stroke-dasharray 1s linear, stroke .3s"
            />
          </svg>
          <div class="odt-timer-text">
            <span class="odt-timer-val">{{ timerSegundos }}s</span>
            <span class="odt-timer-label">próx. actualización</span>
          </div>
        </div>
        <v-btn small color="#2e8aed" dark elevation="0" class="odt-btn-refresh" :loading="loading" @click="refrescarManual">
          <v-icon left size="15">mdi-refresh</v-icon>
          Refrescar
        </v-btn>
      </div>
    </div>

    <div class="odt-kpi-grid">
      <article
        v-for="card in kpiCards"
        :key="card.key"
        class="odt-kpi-card"
        :class="[`odt-kpi-card--${card.tone}`, { 'odt-kpi-card--active': filtroActivo === card.filterKey }]"
        @click="setFiltro(card.filterKey)"
      >
        <div class="odt-kpi-top">
          <div class="odt-kpi-top-main">
            <div class="odt-kpi-icon-pack" :class="`odt-kpi-icon-pack--${card.tone}`">
              <v-icon class="odt-kpi-icon-main" :class="`odt-kpi-icon-main--${card.tone}`">{{ card.icon }}</v-icon>
            </div>
            <p class="odt-kpi-title">{{ card.title }}</p>
          </div>
          <span class="odt-kpi-hint-badge" :class="[`odt-kpi-hint-badge--${card.tone}`, { 'odt-kpi-hint-badge--active': filtroActivo === card.filterKey }]">
            {{ filtroActivo === card.filterKey ? '✓ Activo' : '↓ Filtrar' }}
          </span>
        </div>
        <div class="odt-kpi-value-row">
          <p class="odt-kpi-value-main" :class="`odt-kpi-value-main--${card.tone}`">{{ card.value }}</p>
        </div>
        <p class="odt-kpi-caption">{{ card.caption }}</p>
        <div class="odt-kpi-bar" :style="`width:${card.pct}%; background:var(--tone-${card.tone})`"></div>
      </article>
    </div>

    <template v-if="maquinasCambio.length > 0">
      <div class="odt-section-label odt-section-label--cambio">
        <i class="odt-dot odt-dot--pulse odt-dot--red"></i>
        PREPARAR CAMBIO DE MOLDE
        <span class="odt-count-badge odt-count-badge--red">{{ maquinasCambio.length }} {{ maquinasCambio.length === 1 ? 'máquina' : 'máquinas' }}</span>
      </div>

      <div class="odt-cambio-grid">
        <article
          v-for="m in maquinasCambio"
          :key="m.idMaquina"
          class="odt-cambio-card"
          :class="{ 'odt-cambio-card--urgente': m.odt.minutosSinAtenderCambioMolde > 30 }"
        >
          <div class="odt-cambio-tag">
            <v-icon size="11" color="#fff" style="margin-right:3px">mdi-swap-horizontal-bold</v-icon>
            Cambio de molde pendiente
          </div>

          <div class="odt-cambio-header">
            <div class="odt-cambio-maq-dot" :class="m.odt.minutosSinAtenderCambioMolde > 30 ? 'odt-cambio-maq-dot--urgente' : ''"></div>
            <span class="odt-cambio-maq-name">{{ m.nombre }}</span>
            <span v-if="m.odt.minutosSinAtenderCambioMolde > 30" class="odt-urgente-badge">URGENTE</span>
          </div>

          <div class="odt-cambio-tiempo-wrap" :class="m.odt.minutosSinAtenderCambioMolde > 30 ? 'odt-cambio-tiempo-wrap--urgente' : ''">
            <p class="odt-cambio-tiempo-val">{{ formatMin(m.odt.minutosSinAtenderCambioMolde) }}</p>
            <p class="odt-cambio-tiempo-label">sin atender cambio</p>
          </div>

          <!-- Info de la ODT que terminó -->
          <div class="odt-cambio-odt-info">
            <div class="odt-cambio-odt-row">
              <span class="odt-cambio-odt-lbl">ODT:</span>
              <span class="odt-ot-pill odt-ot-pill--sm">{{ m.odt.ot }}</span>
              <span v-if="m.odt.clave" class="odt-clave-lbl" style="margin-left:4px">{{ m.odt.clave }}</span>
            </div>
            <div class="odt-cambio-odt-row" v-if="m.odt.articulo">
              <span class="odt-cambio-odt-lbl">Art.:</span>
              <span class="odt-cambio-odt-val">{{ m.odt.articulo }}</span>
            </div>
            <div class="odt-cambio-odt-row">
              <span class="odt-cambio-odt-lbl">Piezas:</span>
              <span class="odt-cambio-odt-val odt-cambio-piezas">
                <span class="odt-piezas-prod">{{ m.odt.piezasProducidas }}</span>
                <span class="odt-piezas-sep">/</span>
                <span class="odt-piezas-plan">{{ m.odt.piezasPlaneadas }}</span>
              </span>
            </div>
            <div class="odt-cambio-odt-row" v-if="m.odt.fechaFinRegistrada">
              <span class="odt-cambio-odt-lbl">Terminó:</span>
              <span class="odt-cambio-odt-val odt-cambio-fecha-fin">{{ formatFecha(m.odt.fechaFinRegistrada) }}</span>
            </div>
          </div>

          <!-- Moldes SALE → ENTRA -->
          <div class="odt-cambio-moldes-row">
            <div class="odt-cambio-molde-bloque odt-cambio-molde-bloque--sale">
              <span class="odt-cambio-molde-etq">SALE</span>
              <span class="odt-cambio-molde-codigo">{{ m.odt.codigoMolde }}</span>
              <span class="odt-cambio-molde-ot">{{ m.odt.ot }}</span>
            </div>

            <div class="odt-cambio-flecha">
              <v-icon size="20" color="#9aa6b8">mdi-arrow-right-bold</v-icon>
            </div>

            <div class="odt-cambio-molde-bloque odt-cambio-molde-bloque--entra" v-if="m.siguiente">
              <span class="odt-cambio-molde-etq odt-cambio-molde-etq--entra">ENTRA</span>
              <span class="odt-cambio-molde-codigo odt-cambio-molde-codigo--entra">{{ m.siguiente.codigoMolde }}</span>
              <span class="odt-cambio-molde-ot">{{ m.siguiente.ot }}</span>
            </div>
            <div class="odt-cambio-molde-bloque odt-cambio-molde-bloque--entra" v-else>
              <span class="odt-cambio-molde-etq odt-cambio-molde-etq--entra">ENTRA</span>
              <span class="odt-cambio-molde-codigo odt-cambio-molde-codigo--vacio">Sin siguiente ODT</span>
            </div>
          </div>

          <div class="odt-cambio-footer">
            <v-icon size="12" color="#9aa6b8" style="margin-right:4px">mdi-clock-outline</v-icon>
            <span class="odt-cambio-footer-lbl">Hora sugerida:</span>
            <span class="odt-cambio-footer-val">{{ formatHora(m.odt.horaSugeridaCambioMolde) }}</span>
          </div>
        </article>
      </div>
    </template>

    <div class="odt-section-label" :style="maquinasCambio.length > 0 ? 'margin-top:20px' : ''">
      <i class="odt-dot"></i>
      MÁQUINAS EN PRODUCCIÓN
      <span v-if="filtroActivo !== 'todos'" class="odt-filtro-activo">
        {{ etiquetaFiltroActivo }}
        <button class="odt-clear-filter" @click="setFiltro('todos')">✕ quitar</button>
      </span>
      <span class="odt-count-badge">{{ maquinasVisibles.length }} máquinas</span>
    </div>

    <div v-if="loading && maquinas.length === 0" class="odt-loading-state">
      <v-progress-circular indeterminate color="#2e8aed" size="36" width="3"/>
      <span class="odt-loader-text">Cargando órdenes activas…</span>
    </div>

    <div v-else class="odt-panel">
      <div class="odt-table-wrap">
        <table class="odt-table">
          <thead>
            <tr>
              <th class="odt-th odt-th--sortable" @click="sortBy('nombre')">MÁQUINA {{ sortIcon('nombre') }}</th>
              <th class="odt-th odt-th--sortable" @click="sortBy('ot')">ODT / CLAVE {{ sortIcon('ot') }}</th>
              <th class="odt-th">MOLDE</th>
              <th class="odt-th">ARTÍCULO</th>
              <th class="odt-th odt-th--num">PIEZAS</th>
              <th class="odt-th odt-th--center odt-th--sortable" @click="sortBy('sortTiempo')">TIEMPO {{ sortIcon('sortTiempo') }}</th>
              <th class="odt-th">INICIO</th>
              <th class="odt-th">FIN OBJETIVO</th>
              <th class="odt-th">FIN ESTIMADO</th>
              <th class="odt-th">FIN REAL</th>
              <th class="odt-th odt-th--center">ATRASO</th>
              <th class="odt-th">SIGUIENTE ODT / MOLDE</th>
              <th class="odt-th odt-th--center">ESTADO</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="maquinasVisibles.length === 0">
              <td colspan="13" class="odt-empty">Sin máquinas para mostrar.</td>
            </tr>
            <tr
              v-for="m in maquinasOrdenadas"
              :key="m.idMaquina"
              class="odt-tr"
              :class="{
                'odt-tr--paro':     m.odt.enParo && !m.odt.operacionTerminada,
                'odt-tr--late':     m.odt.atrasada && !m.odt.enParo && !m.odt.operacionTerminada,
                'odt-tr--terminada':m.odt.operacionTerminada,
              }"
            >
              <!-- MÁQUINA -->
              <td class="odt-td odt-td--maq">
                <div class="odt-maq-name-wrap">
                  <span class="odt-maq-status-dot" :class="`odt-maq-status-dot--${modoCard(m.odt)}`"></span>
                  <span class="odt-maq-name">{{ m.nombre }}</span>
                </div>
              </td>

              <!-- ODT / CLAVE -->
              <td class="odt-td odt-td--ot">
                <span class="odt-ot-pill">{{ m.odt.ot }}</span>
                <div v-if="m.odt.clave" class="odt-clave-lbl">{{ m.odt.clave }}</div>
              </td>

              <!-- MOLDE -->
              <td class="odt-td">
                <span class="odt-molde-chip">{{ m.odt.codigoMolde }}</span>
              </td>

              <td class="odt-td odt-td--art">{{ m.odt.articulo }}</td>

              <!-- PIEZAS: producidas / planeadas + barra de progreso -->
              <td class="odt-td odt-td--piezas">
                <div class="odt-piezas-wrap">
                  <div class="odt-piezas-nums">
                    <span class="odt-piezas-prod-val">{{ m.odt.piezasProducidas }}</span>
                    <span class="odt-piezas-sep">/</span>
                    <span class="odt-piezas-plan-val">{{ m.odt.piezasPlaneadas }}</span>
                  </div>
                  <div class="odt-piezas-bar-bg">
                    <div
                      class="odt-piezas-bar-fill"
                      :class="`odt-piezas-bar-fill--${modoCard(m.odt)}`"
                      :style="`width:${piezasPct(m.odt)}%`"
                    ></div>
                  </div>
                  <div class="odt-piezas-restantes">{{ m.odt.piezasRestantes }} rest.</div>
                </div>
              </td>

              <td class="odt-td odt-td--min-col">

                <template v-if="m.odt.operacionTerminada">
                  <div class="odt-tiempo-sin-cambio">
                    <div class="odt-tsm-label">
                      <v-icon size="10" color="#cc2222" style="margin-right:2px">mdi-swap-horizontal-bold</v-icon>
                      sin cambio
                    </div>
                    <div class="odt-tsm-val"
                         :class="m.odt.minutosSinAtenderCambioMolde > 60 ? 'odt-tsm-val--urgente'
                                 : m.odt.minutosSinAtenderCambioMolde > 0  ? 'odt-tsm-val--warn' : ''">
                      {{ formatMin(m.odt.minutosSinAtenderCambioMolde) }}
                    </div>
                  </div>
                </template>

                <template v-else-if="m.odt.minutosAtrasoProduccion > 0">
                  <div class="odt-tiempo-atrasado">
                    <div class="odt-ta-label">
                      <v-icon size="10" color="#f08b00" style="margin-right:2px">mdi-clock-alert-outline</v-icon>
                      retrasada
                    </div>
                    <div class="odt-ta-val"
                         :class="m.odt.minutosAtrasoProduccion > 60 ? 'odt-ta-val--late' : 'odt-ta-val--warn'">
                      +{{ formatMin(m.odt.minutosAtrasoProduccion) }}
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="odt-tiempo-restante">
                    <div class="odt-tr-label">para terminar</div>
                    <div class="odt-tr-val" :class="`odt-min-val--${modoCard(m.odt)}`">
                      {{ formatMin(m.odt.minutosRestantes) }}
                    </div>
                  </div>
                </template>

              </td>

              <!-- FECHA INICIO -->
              <td class="odt-td odt-td--fecha">{{ formatFecha(m.odt.fechaInicio) }}</td>

              <!-- FIN OBJETIVO -->
              <td class="odt-td odt-td--fecha">{{ formatFecha(m.odt.fechaFinObjetivo) }}</td>

              <!-- FIN ESTIMADO -->
              <td class="odt-td odt-td--fecha odt-td--fecha-est">{{ formatFecha(m.odt.fechaFinEstimada) }}</td>

              <!-- FIN REAL -->
              <td class="odt-td odt-td--fecha odt-td--fecha-real">
                <template v-if="m.odt.fechaFinRegistrada">
                  {{ formatFecha(m.odt.fechaFinRegistrada) }}
                </template>
                <span v-else class="odt-no-sig">—</span>
              </td>

              <!-- ATRASO: lee directamente del backend, sin recalcular en front -->
              <td class="odt-td odt-td--center">
                <template v-if="infoAtraso(m.odt).tipo === 'ok'">
                  <span class="odt-delta-pill odt-delta-pill--ok">
                    <v-icon size="10" style="margin-right:2px">mdi-check</v-icon>En tiempo
                  </span>
                </template>
                <template v-else>
                  <div class="odt-delta-wrap">
                    <span class="odt-delta-pill" :class="`odt-delta-pill--${infoAtraso(m.odt).tono}`">
                      <v-icon v-if="infoAtraso(m.odt).tipo === 'cambio'" size="10" style="margin-right:2px">mdi-swap-horizontal-bold</v-icon>
                      +{{ infoAtraso(m.odt).label }}
                    </span>
                    <span class="odt-delta-sub">{{ infoAtraso(m.odt).sub }}</span>
                  </div>
                </template>
              </td>

              <!-- SIGUIENTE ODT / MOLDE -->
              <td class="odt-td">
                <template v-if="m.siguiente">
                  <div style="display:flex;align-items:center;gap:5px">
                    <v-icon size="11" color="#2e8aed">mdi-arrow-right</v-icon>
                    <span class="odt-ot-pill odt-ot-pill--sm">{{ m.siguiente.ot }}</span>
                  </div>
                  <div v-if="m.siguiente.clave" class="odt-clave-lbl">{{ m.siguiente.clave }}</div>
                  <div class="odt-siguiente-molde">
                    <v-icon size="10" color="#9aa6b8">mdi-shape-outline</v-icon>
                    {{ m.siguiente.codigoMolde }}
                  </div>
                </template>
                <span v-else class="odt-no-sig">—</span>
              </td>

              <!-- ESTADO -->
              <td class="odt-td odt-td--center">
                <div class="odt-estado-badges">
                  <span v-if="m.odt.operacionTerminada" class="odt-badge odt-badge--green  odt-badge--sm">TERMINADA</span>
                  <span v-if="m.odt.vencida"            class="odt-badge odt-badge--red    odt-badge--sm">VENCIDA</span>
                  <span v-if="m.odt.atrasada && !m.odt.operacionTerminada" class="odt-badge odt-badge--amber odt-badge--sm">ATRASADA</span>
                  <span v-if="m.odt.enParo"             class="odt-badge odt-badge--slate  odt-badge--sm">EN PARO</span>
                  <span v-if="!m.odt.vencida && !m.odt.atrasada && !m.odt.enParo && !m.odt.operacionTerminada"
                    class="odt-badge odt-badge--green odt-badge--sm">OK</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="odt-footer">
      <span>CAZEL · Control de Producción — Moldes EP</span>
      <span>Actualizado: {{ ultimaActualizacion }}</span>
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "OdtMoldesDashboard",
  props: {
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: false,
      search: "",
      maquinas: [],
      filtroActivo: "todos",
      sortKey: "sortTiempo",
      sortDesc: true,
      ultimaActualizacion: "—",
      timerSegundos: 300,
      timerIntervalId: null,
    };
  },
  computed: {
    maquinasCambio() {
      return this.maquinas
        .filter(m => m.odt.cambioMoldePendiente)
        .sort((a, b) => b.odt.minutosSinAtenderCambioMolde - a.odt.minutosSinAtenderCambioMolde);
    },
    maquinasSinCambio() {
      return this.maquinas.filter(m => !m.odt.cambioMoldePendiente);
    },
    resumen() {
      const m = this.maquinasSinCambio;
      return {
        maquinas:  m.length,
        enTiempo:  m.filter(x => !x.odt.enParo && !x.odt.atrasada).length,
        atrasadas: m.filter(x => x.odt.atrasada && !x.odt.enParo).length,
        enParo:    m.filter(x => x.odt.enParo).length,
      };
    },
    kpiCards() {
      const r = this.resumen;
      const total = Math.max(1, r.maquinas);
      return [
        { key: "maquinas",  filterKey: "todos",     tone: "blue",  icon: "mdi-cog-outline",         value: r.maquinas,  caption: "Máquinas activas en producción",        pct: 100 },
        { key: "enTiempo",  filterKey: "enTiempo",  tone: "green", icon: "mdi-check-circle-outline", value: r.enTiempo,  caption: "Corriendo dentro del tiempo objetivo",  pct: Math.round((r.enTiempo  / total) * 100) },
        { key: "atrasadas", filterKey: "atrasadas", tone: "amber", icon: "mdi-clock-alert-outline",  value: r.atrasadas, caption: "Con retraso acumulado por paros",        pct: Math.round((r.atrasadas / total) * 100) },
        { key: "enParo",    filterKey: "enParo",    tone: "slate", icon: "mdi-pause-circle-outline", value: r.enParo,    caption: "Con paro activo en este momento",        pct: Math.round((r.enParo    / total) * 100) },
      ];
    },
    maquinasFiltradas() {
      let base = this.maquinasSinCambio;
      if (this.filtroActivo === "enTiempo")  base = base.filter(x => !x.odt.enParo && !x.odt.atrasada);
      if (this.filtroActivo === "atrasadas") base = base.filter(x => x.odt.atrasada && !x.odt.enParo);
      if (this.filtroActivo === "enParo")    base = base.filter(x => x.odt.enParo);
      if (this.search) {
        const q = this.search.toLowerCase();
        base = base.filter(x =>
          x.nombre.toLowerCase().includes(q) ||
          x.odt.ot.toLowerCase().includes(q) ||
          (x.odt.clave || "").toLowerCase().includes(q) ||
          (x.odt.codigoMolde || "").toLowerCase().includes(q) ||
          (x.odt.articulo || "").toLowerCase().includes(q)
        );
      }
      return base;
    },
    maquinasVisibles() {
      return this.maquinasFiltradas;
    },
    maquinasOrdenadas() {
      const arr = this.maquinasFiltradas.map(m => ({
        ...m,
        
        sortTiempo: m.odt.operacionTerminada
          ? this.toNumber(m.odt.minutosSinAtenderCambioMolde)
          : m.odt.minutosAtrasoProduccion > 0
            ? this.toNumber(m.odt.minutosAtrasoProduccion)
            : this.toNumber(m.odt.minutosRestantes),
        sortPrioridad:
          (m.odt.cambioMoldePendiente  ? 4 : 0) +
          (m.odt.enParo                ? 3 : 0) +
          (m.odt.atrasada              ? 2 : 0) +
          (m.odt.vencida               ? 1 : 0),
      }));
      const k = this.sortKey;
      const d = this.sortDesc ? -1 : 1;
      arr.sort((a, b) => {
        let va = a[k] ?? a.odt?.[k] ?? "", vb = b[k] ?? b.odt?.[k] ?? "";
        if (typeof va === "string") return va.localeCompare(vb) * d;
        return (va - vb) * d;
      });
      return arr;
    },
    etiquetaFiltroActivo() {
      const map = { enTiempo: "EN TIEMPO", atrasadas: "ATRASADAS", enParo: "EN PARO" };
      return map[this.filtroActivo] || "";
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(val) {
        if (val) {
          if (this.maquinas.length === 0 && !this.loading) this.initialize();
          this.iniciarAutoRefresh();
        } else {
          this.detenerAutoRefresh();
        }
      },
    },
  },
  beforeDestroy() {
    this.detenerAutoRefresh();
  },
  methods: {
    toNumber(val) { const n = Number(val); return Number.isFinite(n) ? n : 0; },
    toArray(val)  { return Array.isArray(val) ? val : []; },

    formatFecha(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (!Number.isFinite(d.getTime())) return "—";
      return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
    },
    formatHora(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (!Number.isFinite(d.getTime())) return "—";
      return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
    },
    formatMin(min) {
      const m = Math.round(this.toNumber(min));
      if (m <= 0) return "0m";
      if (m < 60) return `${m}m`;
      const h = Math.floor(m / 60), r = m % 60;
      return r > 0 ? `${h}h ${r}m` : `${h}h`;
    },

    piezasPct(odt) {
      const plan = this.toNumber(odt.piezasPlaneadas);
      const prod = this.toNumber(odt.piezasProducidas);
      if (plan <= 0) return 0;
      return Math.min(100, Math.round((prod / plan) * 100));
    },

    modoCard(odt) {
      if (odt.cambioMoldePendiente) return "cambio";
      if (odt.enParo)               return "paro";
      if (odt.atrasada)             return "late";
      return "ok";
    },

    infoAtraso(odt) {
      // ODT terminada → el indicador es MinutosSinAtenderCambioMolde
      if (odt.operacionTerminada) {
        const mins = this.toNumber(odt.minutosSinAtenderCambioMolde);
        return {
          tipo:  "cambio",
          mins,
          label: this.formatMin(mins),
          tono:  mins > 60 ? "late" : mins > 0 ? "warn" : "ok",
          sub:   "sin cambio de molde",
        };
      }
      // ODT activa con atraso de producción (ya pasó su FechaFinEstimada)
      if (odt.minutosAtrasoProduccion > 0) {
        const mins = this.toNumber(odt.minutosAtrasoProduccion);
        return {
          tipo:  "atraso",
          mins,
          label: this.formatMin(mins),
          tono:  mins > 60 ? "late" : "warn",
          sub:   "pasó fin estimado",
        };
      }
      // ODT activa, en tiempo
      return { tipo: "ok", mins: 0, label: "—", tono: "ok", sub: "" };
    },

    setFiltro(valor) {
      this.filtroActivo = this.filtroActivo === valor ? "todos" : valor;
    },
    sortBy(key) {
      if (this.sortKey === key) this.sortDesc = !this.sortDesc;
      else { this.sortKey = key; this.sortDesc = true; }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return "⇅";
      return this.sortDesc ? "↓" : "↑";
    },

    iniciarAutoRefresh() {
      this.detenerAutoRefresh();
      this.timerSegundos = 300;
      this.timerIntervalId = setInterval(() => {
        this.timerSegundos -= 1;
        if (this.timerSegundos <= 0) { this.timerSegundos = 300; this.refrescar(); }
      }, 1000);
    },
    detenerAutoRefresh() {
      if (this.timerIntervalId) { clearInterval(this.timerIntervalId); this.timerIntervalId = null; }
    },
    refrescarManual() { this.timerSegundos = 300; this.refrescar(); },
    async initialize() { await this.refrescar(); },

    async refrescar() {
      this.loading = true;
      try {
        const response = await axios.get("/TrabajoOrdenes/OdtMoldesEstado");
        const payload  = response?.data || {};
        const ordenes  = this.toArray(payload?.Ordenes ?? payload?.ordenes);
        const mapped   = ordenes.map(x => this.mapOrden(x));

        const maqMap = new Map();
        mapped.forEach(ord => {
          const key    = ord.idMaquinaIns != null ? `maq-${ord.idMaquinaIns}` : `sin-maq-${ord.idOtd}`;
          const nombre = ord.maquina || "Sin máquina";
          if (!maqMap.has(key)) {
            maqMap.set(key, { nombre, ord });
          }
        });

        const maquinas = [];
        maqMap.forEach((val) => {
          const ord = val.ord;

          const siguiente = ord.siguienteIdTrabajoOrden
            ? {
                ot:          `ODT-${ord.siguienteIdTrabajoOrden}`,
                clave:       ord.siguienteClave        ?? "",
                codigoMolde: ord.siguienteCodigoMolde  ?? "N/D",
                articulo:    ord.siguienteArticulo      ?? "N/D",
              }
            : null;

          maquinas.push({
            idMaquina: val.nombre + (ord.idMaquinaIns ?? ord.idOtd),
            nombre:    val.nombre,
            odt:       ord,
            siguiente,
          });
        });

        const prio = o =>
          (o.cambioMoldePendiente ? 4 : 0) +
          (o.enParo    ? 3 : 0) +
          (o.atrasada  ? 2 : 0) +
          (o.vencida   ? 1 : 0);
        maquinas.sort((a, b) => prio(b.odt) - prio(a.odt));

        this.maquinas = maquinas;
        const now = new Date();
        this.ultimaActualizacion = `${now.toLocaleDateString("es-MX")} ${now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}`;
      } finally {
        this.loading = false;
      }
    },

    mapOrden(x) {
      const n = k => {
        const val = x?.[k] ?? x?.[k[0].toLowerCase() + k.slice(1)];
        return this.toNumber(val);
      };
      const s = k => x?.[k] ?? x?.[k[0].toLowerCase() + k.slice(1)] ?? null;

      return {
        idOt:         n("IdTrabajoOrden"),
        idOtd:        n("IdTrabajoOrdenDetalle"),
        idMaquinaIns: n("IdMaquinaIns") || null,
        idMaquina:    n("IdMaquina")    || null,

        ot:       `ODT-${n("IdTrabajoOrden")}`,
        clave:    s("Clave")    ?? "",
        articulo: (() => {
          const nom = s("Articulo")       ?? "N/D";
          const cod = s("CodigoArticulo") ?? null;
          return cod ? `[${cod}] ${nom}` : nom;
        })(),
        codigoMolde: s("CodigoMolde") ?? "N/D",
        maquina:     s("Maquina"),

        // Estados
        enParo:             Boolean(s("EnParo")),
        atrasada:           Boolean(s("Atrasada")),
        vencida:            Boolean(s("Vencida")),
        operacionTerminada: Boolean(s("OperacionTerminada")),

        // Cambio de molde
        cambioMoldePendiente:         Boolean(s("CambioMoldePendiente")),
        minutosSinAtenderCambioMolde: n("MinutosSinAtenderCambioMolde"),
        horaSugeridaCambioMolde:      s("HoraSugeridaCambioMolde"),

        // Piezas
        piezasPlaneadas:  n("PiezasPlaneadas"),
        piezasProducidas: n("PiezasProducidas"),
        piezasRestantes:  n("PiezasRestantes"),


        minutosRestantes:        n("MinutosRestantes"),
        minutosAtrasoProduccion: n("MinutosAtrasoProduccion"),
        minutosParoAcumulados:   n("MinutosParoAcumulados"),

        // Fechas
        fechaInicio:          s("FechaInicio"),
        fechaFinObjetivo:     s("FechaFinObjetivo"),
        fechaFinEstimada:     s("FechaFinEstimada"),
        fechaFinRegistrada:   s("FechaFinRegistrada"),   // ← NUEVO: fin real registrado
        fechaEntrega:         s("FechaEntrega"),

        // Siguiente ODT
        siguienteIdTrabajoOrden:        n("SiguienteIdTrabajoOrden") || null,
        siguienteIdTrabajoOrdenDetalle: n("SiguienteIdTrabajoOrdenDetalle") || null,
        siguienteClave:                 s("SiguienteClave"),
        siguienteArticulo:              s("SiguienteArticulo"),
        siguienteCodigoMolde:           s("SiguienteCodigoMolde"),
      };
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');

.odt-root {
  --tone-blue:   #2e8aed;
  --tone-green:  #1fb36b;
  --tone-amber:  #f08b00;
  --tone-slate:  #ee5159;
  --tone-red:    #cc2222;
  --tone-violet: #6d50d7;
  font-family: "Montserrat", "Segoe UI", sans-serif;
  background: #f0f4f9;
  min-height: 100vh;
  padding: 0 0 40px;
  color: #1c2639;
}

.odt-controls-bar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; padding: 14px 24px;
  background: #fff; border-bottom: 1px solid #dde6f0;
  box-shadow: 0 2px 8px rgba(24,43,70,.06);
}
.odt-controls-left, .odt-controls-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.odt-search-wrap { position: relative; display: flex; align-items: center; }
.odt-search-icon { position: absolute; left: 9px; pointer-events: none; }
.odt-search {
  background: #f4f8fd; border: 1px solid #c8d7ea; border-radius: 999px;
  color: #1c2639; font-family: "Montserrat", sans-serif; font-size: 12.5px; font-weight: 500;
  padding: 7px 32px 7px 32px; outline: none; width: 240px; transition: border-color .2s;
}
.odt-search:focus { border-color: #2e8aed; background: #fff; }
.odt-search::placeholder { color: #9aa6b8; }
.odt-search-clear { position: absolute; right: 10px; background: none; border: none; color: #9aa6b8; cursor: pointer; font-size: 11px; }
.odt-select {
  background: #f4f8fd; border: 1px solid #c8d7ea; border-radius: 999px;
  color: #1c2639; font-family: "Montserrat", sans-serif; font-size: 12.5px; font-weight: 600;
  padding: 7px 14px; outline: none; cursor: pointer; transition: border-color .2s;
}
.odt-select:focus { border-color: #2e8aed; }
.odt-refresh-timer {
  display: flex; align-items: center; gap: 8px;
  background: #f4f8fd; border: 1px solid #dde6f0; border-radius: 12px;
  padding: 6px 12px 6px 6px; transition: all .3s;
}
.odt-refresh-timer--soon { border-color: #f6c97a; background: #fffbf0; }
.odt-timer-circle { width: 30px; height: 30px; flex-shrink: 0; }
.odt-timer-text   { display: flex; flex-direction: column; }
.odt-timer-val    { font-size: 13px; font-weight: 800; color: #1c2639; line-height: 1; }
.odt-timer-label  { font-size: 9px; letter-spacing: .8px; color: #9aa6b8; margin-top: 1px; }
.odt-btn-refresh  { border-radius: 999px !important; font-family: "Montserrat", sans-serif !important; font-size: 12.5px !important; font-weight: 700 !important; letter-spacing: .5px !important; }

.odt-kpi-grid {
  display: grid; grid-template-columns: repeat(4, minmax(0,1fr));
  gap: 14px; padding: 18px 24px 0;
}
.odt-kpi-card {
  background: linear-gradient(180deg, #f8fbff 0%, #f2f6fc 100%);
  border: 1px solid #c4d5ea; border-radius: 22px;
  padding: 14px 16px 16px; display: flex; flex-direction: column;
  min-height: 150px; cursor: pointer; user-select: none;
  position: relative; overflow: hidden;
  transition: box-shadow .18s, transform .12s, border-color .18s;
}
.odt-kpi-card:hover  { box-shadow: 0 6px 20px rgba(24,43,70,.11); transform: translateY(-1px); }
.odt-kpi-card:active { transform: scale(.98); }
.odt-kpi-card--blue  { border-color: #8fc1f3; }
.odt-kpi-card--green { border-color: #7fcfa7; }
.odt-kpi-card--amber { border-color: #f0be82; }
.odt-kpi-card--slate { border-color: #f0b2b7; }
.odt-kpi-card--blue.odt-kpi-card--active  { background: #ebf4ff; box-shadow: 0 0 0 2px #2e8aed; border-color: #2e8aed; }
.odt-kpi-card--green.odt-kpi-card--active { background: #edfdf5; box-shadow: 0 0 0 2px #1fb36b; border-color: #1fb36b; }
.odt-kpi-card--amber.odt-kpi-card--active { background: #fff8ed; box-shadow: 0 0 0 2px #f08b00; border-color: #f08b00; }
.odt-kpi-card--slate.odt-kpi-card--active { background: #fff1f2; box-shadow: 0 0 0 2px #ee5159; border-color: #ee5159; }
.odt-kpi-bar { position: absolute; bottom: 0; left: 0; height: 3px; border-radius: 0 3px 3px 0; transition: width .6s ease; }
.odt-kpi-top      { display: flex; align-items: flex-start; justify-content: space-between; gap: 6px; margin-bottom: 6px; }
.odt-kpi-top-main { display: flex; align-items: center; gap: 10px; min-width: 0; }
.odt-kpi-icon-pack {
  align-items: center; border-radius: 14px; display: inline-flex;
  height: 46px; justify-content: center; width: 46px; flex-shrink: 0;
}
.odt-kpi-icon-pack--blue  { background: linear-gradient(145deg,#d8e9fb,#c6def7); }
.odt-kpi-icon-pack--green { background: linear-gradient(145deg,#d4f4e5,#b6edcd); }
.odt-kpi-icon-pack--amber { background: linear-gradient(145deg,#fde7c7,#f8d8a4); }
.odt-kpi-icon-pack--slate { background: linear-gradient(145deg,#f6d9dd,#f2c8cd); }
.odt-kpi-icon-main { font-size: 1.35rem !important; }
.odt-kpi-icon-main--blue  { color: #2e8aed !important; }
.odt-kpi-icon-main--green { color: #1fb36b !important; }
.odt-kpi-icon-main--amber { color: #f08b00 !important; }
.odt-kpi-icon-main--slate { color: #ee5159 !important; }
.odt-kpi-title { color: #1c2639; font-size: 0.88rem; font-weight: 700; line-height: 1.2; margin: 0; }
.odt-kpi-hint-badge {
  font-size: 9px; font-weight: 700; letter-spacing: .5px;
  padding: 2px 8px; border-radius: 999px; white-space: nowrap; flex-shrink: 0;
  background: #e8edf6; color: #657993;
}
.odt-kpi-hint-badge--active { background: #d0e6fb; color: #1565c0; }
.odt-kpi-value-row {
  align-items: center; display: flex; gap: 6px; inset: 26px 0 18px;
  justify-content: center; margin: 0; pointer-events: none; position: absolute;
}
.odt-kpi-value-main { font-size: 62px !important; font-weight: 800; line-height: .92; margin: 0; }
.odt-kpi-value-main--blue  { color: #2d84da; }
.odt-kpi-value-main--green { color: #1fb36b; }
.odt-kpi-value-main--amber { color: #f08b00; }
.odt-kpi-value-main--slate { color: #ee5159; }
.odt-kpi-caption {
  bottom: 16px; color: #657993; font-size: 0.78rem; font-weight: 500;
  left: 16px; line-height: 1.2; margin: 0; position: absolute; right: 16px;
}

/* ─── Section labels ─── */
.odt-section-label {
  display: flex; align-items: center; gap: 8px;
  padding: 18px 24px 8px;
  font-size: 10.5px; font-weight: 800; letter-spacing: 2.5px; color: #657993;
}
.odt-section-label--cambio { color: #8a1a1a; letter-spacing: 2px; }
.odt-dot {
  width: 7px; height: 7px; border-radius: 50%; background: #2e8aed; flex-shrink: 0; display: inline-block;
}
.odt-dot--red   { background: #cc2222; }
.odt-dot--pulse { animation: odt-pulse-red 1.4s ease-out infinite; }
@keyframes odt-pulse-red {
  0%   { box-shadow: 0 0 0 0 rgba(204,34,34,.6); }
  70%  { box-shadow: 0 0 0 9px rgba(204,34,34,0); }
  100% { box-shadow: 0 0 0 0 rgba(204,34,34,0); }
}
.odt-count-badge {
  margin-left: auto; background: #e8edf6; color: #4a5e7a;
  font-size: 9.5px; font-weight: 700; padding: 2px 10px; border-radius: 999px;
}
.odt-count-badge--red { background: #ffd6d6; color: #8a0000; }
.odt-filtro-activo {
  display: flex; align-items: center; gap: 6px;
  background: #fde7c7; color: #a05000;
  font-size: 9.5px; font-weight: 700; padding: 2px 10px; border-radius: 999px;
}
.odt-clear-filter {
  background: none; border: none; color: #c06000; cursor: pointer;
  font-size: 9.5px; font-weight: 700; font-family: inherit; padding: 0;
}
.odt-clear-filter:hover { text-decoration: underline; }

/* ─── Cambio cards ─── */
.odt-cambio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 14px; padding: 0 24px 0;
}
.odt-cambio-card {
  background: linear-gradient(160deg, #fff7f7 0%, #ffe8e8 100%);
  border: 2px solid #f09090; border-radius: 18px;
  padding: 18px 18px 14px; position: relative;
  box-shadow: 0 4px 18px rgba(204,34,34,.10);
  transition: box-shadow .2s, transform .15s;
  animation: odt-cambio-flash 3s ease-in-out infinite;
}
.odt-cambio-card:hover { box-shadow: 0 6px 24px rgba(204,34,34,.20); transform: translateY(-1px); }
.odt-cambio-card--urgente {
  border-color: #cc2222;
  background: linear-gradient(160deg, #fff0f0 0%, #ffd8d8 100%);
  animation: odt-cambio-flash-urgente 1.8s ease-in-out infinite;
}
@keyframes odt-cambio-flash {
  0%,100% { box-shadow: 0 4px 18px rgba(204,34,34,.10); }
  50%     { box-shadow: 0 4px 24px rgba(204,34,34,.22); }
}
@keyframes odt-cambio-flash-urgente {
  0%,100% { box-shadow: 0 4px 18px rgba(204,34,34,.22); border-color: #cc2222; }
  50%     { box-shadow: 0 4px 28px rgba(204,34,34,.40); border-color: #ff2020; }
}
.odt-cambio-tag {
  position: absolute; top: -1px; left: 18px;
  background: #cc2222; color: #fff;
  font-size: 9px; font-weight: 800; letter-spacing: 2px;
  padding: 3px 11px; border-radius: 0 0 8px 8px;
  display: flex; align-items: center;
}
.odt-cambio-header {
  display: flex; align-items: center; gap: 8px;
  margin-top: 14px; margin-bottom: 8px;
}
.odt-cambio-maq-dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: #cc2222; flex-shrink: 0;
  animation: odt-dot-blink .9s ease-in-out infinite;
}
.odt-cambio-maq-dot--urgente { background: #ff2020; }
@keyframes odt-dot-blink { 0%,100%{opacity:1} 50%{opacity:.25} }
.odt-cambio-maq-name  { font-size: 15px; font-weight: 800; color: #1c2639; flex: 1; }
.odt-urgente-badge {
  font-size: 9px; font-weight: 800; letter-spacing: 1.5px;
  background: #cc2222; color: #fff; padding: 2px 8px; border-radius: 999px;
}
.odt-cambio-tiempo-wrap {
  text-align: center;
  background: rgba(204,34,34,.07); border: 1px solid rgba(204,34,34,.18);
  border-radius: 12px; padding: 8px 12px; margin-bottom: 10px;
}
.odt-cambio-tiempo-wrap--urgente { background: rgba(204,34,34,.13); border-color: rgba(204,34,34,.35); }
.odt-cambio-tiempo-val {
  font-size: 28px; font-weight: 800; color: #cc2222; line-height: 1; margin: 0;
}
.odt-cambio-tiempo-wrap--urgente .odt-cambio-tiempo-val { font-size: 34px; }
.odt-cambio-tiempo-label {
  font-size: 9px; letter-spacing: 1.5px; font-weight: 700;
  color: #cc2222; opacity: .7; margin: 2px 0 0;
}

/* ODT info dentro de cambio card */
.odt-cambio-odt-info {
  background: rgba(255,255,255,.6);
  border: 1px solid rgba(204,34,34,.1);
  border-radius: 10px; padding: 8px 10px; margin-bottom: 10px;
  display: flex; flex-direction: column; gap: 4px;
}
.odt-cambio-odt-row {
  display: flex; align-items: center; gap: 6px; font-size: 11px;
}
.odt-cambio-odt-lbl {
  font-size: 9.5px; font-weight: 700; letter-spacing: .5px;
  color: #9aa6b8; min-width: 32px;
}
.odt-cambio-odt-val { color: #1c2639; font-weight: 600; font-size: 11.5px; }
.odt-cambio-piezas  { display: flex; align-items: baseline; gap: 2px; }
.odt-piezas-prod    { font-size: 14px; font-weight: 800; color: #1fb36b; }
.odt-piezas-sep     { font-size: 11px; color: #9aa6b8; }
.odt-piezas-plan    { font-size: 12px; font-weight: 700; color: #657993; }
.odt-cambio-fecha-fin { font-weight: 800; color: #cc2222; }

.odt-cambio-moldes-row {
  display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
}
.odt-cambio-molde-bloque {
  flex: 1; border-radius: 10px; padding: 8px 10px;
  display: flex; flex-direction: column; gap: 2px;
}
.odt-cambio-molde-bloque--sale  { background: rgba(204,34,34,.07); border: 1px solid rgba(204,34,34,.2); }
.odt-cambio-molde-bloque--entra { background: rgba(46,138,237,.07); border: 1px solid rgba(46,138,237,.25); }
.odt-cambio-molde-etq {
  font-size: 8.5px; font-weight: 800; letter-spacing: 1.5px;
  color: #cc2222; margin-bottom: 1px;
}
.odt-cambio-molde-etq--entra  { color: #1565c0; }
.odt-cambio-molde-codigo      { font-size: 14px; font-weight: 800; color: #1c2639; letter-spacing: .5px; line-height: 1; }
.odt-cambio-molde-codigo--entra { color: #1565c0; }
.odt-cambio-molde-codigo--vacio { font-size: 11px; color: #9aa6b8; font-weight: 600; }
.odt-cambio-molde-ot   { font-size: 10px; color: #9aa6b8; font-weight: 600; }
.odt-cambio-flecha     { flex-shrink: 0; }
.odt-cambio-footer {
  display: flex; align-items: center; font-size: 11px; color: #657993;
  padding-top: 8px; border-top: 1px solid rgba(204,34,34,.12);
}
.odt-cambio-footer-lbl { font-weight: 600; margin-right: 4px; }
.odt-cambio-footer-val { font-weight: 800; color: #1c2639; font-size: 12px; }

/* ─── Loading ─── */
.odt-loading-state {
  display: flex; align-items: center; justify-content: center;
  gap: 12px; padding: 60px 24px;
}
.odt-loader-text { font-size: 12.5px; color: #657993; font-weight: 600; }

/* ─── Table panel ─── */
.odt-panel {
  margin: 0 24px;
  background: linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
  border: 1px solid #d8e2ef; border-radius: 18px;
  overflow: hidden; box-shadow: 0 4px 16px rgba(24,43,70,.07);
}
.odt-table-wrap { overflow-x: auto; }
.odt-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.odt-th {
  background: #f4f8fd; color: #314864;
  font-size: 10px; font-weight: 800; letter-spacing: 1.2px;
  padding: 10px 13px; text-align: left; white-space: nowrap;
  border-bottom: 2px solid #dde6f0; user-select: none;
}
.odt-th--sortable       { cursor: pointer; }
.odt-th--sortable:hover { background: #edf2fb; color: #1c2639; }
.odt-th--num            { text-align: right; }
.odt-th--center         { text-align: center; }

/* Row states */
.odt-tr { border-bottom: 1px solid #edf2f8; transition: background .1s; }
.odt-tr:hover         { background: #f8fafd; }
.odt-tr--paro         { background: #fff8f8; border-left: 3px solid #ee5159; }
.odt-tr--late         { background: #fffbf0; border-left: 3px solid #f08b00; }
.odt-tr--terminada    { background: #f2fbf7; border-left: 3px solid #1fb36b; }

.odt-td           { padding: 10px 13px; color: #314864; vertical-align: middle; }
.odt-td--num      { text-align: right; font-variant-numeric: tabular-nums; }
.odt-td--min      { font-weight: 800; font-size: 14px; text-align: right; }
.odt-td--maq      { white-space: nowrap; min-width: 130px; }
.odt-td--ot       { white-space: nowrap; }
.odt-td--art      { min-width: 160px; font-size: 12px; color: #657993; }
.odt-td--fecha    { white-space: nowrap; font-size: 12px; }
.odt-td--fecha-est  { font-weight: 700; color: #2e8aed; }
.odt-td--fecha-real { font-weight: 700; color: #1fb36b; }
.odt-td--center   { text-align: center; }
.odt-td--piezas   { min-width: 120px; }

/* Machine name + status dot */
.odt-maq-name-wrap  { display: flex; align-items: center; gap: 7px; }
.odt-maq-status-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.odt-maq-status-dot--ok     { background: #1fb36b; }
.odt-maq-status-dot--late   { background: #f08b00; }
.odt-maq-status-dot--paro   { background: #ee5159; }
.odt-maq-status-dot--cambio { background: #cc2222; animation: odt-dot-blink .9s ease-in-out infinite; }
.odt-maq-name { font-size: 13px; font-weight: 800; color: #1c2639; }

/* Pills & chips */
.odt-ot-pill {
  font-size: 12px; font-weight: 800; background: #e8edf6; color: #1c2639;
  padding: 2px 8px; border-radius: 6px; letter-spacing: .5px;
}
.odt-ot-pill--sm { font-size: 10.5px; padding: 1px 6px; }
.odt-clave-lbl   { font-size: 10.5px; color: #2e8aed; font-weight: 700; margin-top: 2px; }
.odt-molde-chip {
  font-size: 11.5px; font-weight: 800;
  background: linear-gradient(145deg,#d8e9fb,#c6def7);
  color: #1565c0; border: 1px solid #b3d1f7;
  padding: 2px 9px; border-radius: 6px; letter-spacing: .5px;
}

/* Piezas progress */
.odt-piezas-wrap     { display: flex; flex-direction: column; gap: 3px; min-width: 100px; }
.odt-piezas-nums     { display: flex; align-items: baseline; gap: 3px; }
.odt-piezas-prod-val { font-size: 14px; font-weight: 800; color: #1fb36b; }
.odt-piezas-sep      { font-size: 11px; color: #c8d7ea; }
.odt-piezas-plan-val { font-size: 12px; font-weight: 700; color: #657993; }
.odt-piezas-bar-bg   { height: 4px; background: #e8edf6; border-radius: 999px; overflow: hidden; }
.odt-piezas-bar-fill {
  height: 100%; border-radius: 999px;
  transition: width .6s ease;
}
.odt-piezas-bar-fill--ok     { background: #1fb36b; }
.odt-piezas-bar-fill--late   { background: #f08b00; }
.odt-piezas-bar-fill--paro   { background: #ee5159; }
.odt-piezas-bar-fill--cambio { background: #cc2222; }
.odt-piezas-restantes { font-size: 9.5px; color: #9aa6b8; font-weight: 600; }

.odt-td--min-col   { min-width: 110px; text-align: center; vertical-align: middle; }

.odt-tiempo-atrasado { display: flex; flex-direction: column; align-items: center; gap: 1px; }
.odt-ta-label {
  display: flex; align-items: center;
  font-size: 8.5px; font-weight: 700; letter-spacing: .8px;
  color: #f08b00; text-transform: uppercase;
}
.odt-ta-val        { font-size: 18px; font-weight: 800; line-height: 1.1; }
.odt-ta-val--warn  { color: #f08b00; }
.odt-ta-val--late  { color: #cc2222; font-size: 20px; animation: odt-urgente-pulse 1.2s ease-in-out infinite; }


.odt-tiempo-restante { display: flex; flex-direction: column; align-items: center; gap: 1px; }
.odt-tr-label        { font-size: 8.5px; font-weight: 700; letter-spacing: .8px; color: #9aa6b8; text-transform: uppercase; }
.odt-tr-val          { font-size: 18px; font-weight: 800; line-height: 1.1; }

/* ODT terminada → tiempo sin cambio */
.odt-tiempo-sin-cambio { display: flex; flex-direction: column; align-items: center; gap: 1px; }
.odt-tsm-label {
  display: flex; align-items: center;
  font-size: 8.5px; font-weight: 700; letter-spacing: .8px;
  color: #cc2222; text-transform: uppercase;
}
.odt-tsm-val            { font-size: 18px; font-weight: 800; color: #cc2222; line-height: 1.1; }
.odt-tsm-val--urgente   { font-size: 20px; color: #a80000; animation: odt-urgente-pulse 1.2s ease-in-out infinite; }
@keyframes odt-urgente-pulse {
  0%,100% { opacity: 1; }
  50%     { opacity: .55; }
}


.odt-min-val--late   { color: #f08b00; }
.odt-min-val--paro   { color: #ee5159; }
.odt-min-val--cambio { color: #cc2222; }

.odt-delta-wrap { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.odt-delta-pill {
  display: inline-flex; align-items: center;
  font-size: 10.5px; font-weight: 800;
  padding: 3px 10px; border-radius: 999px; white-space: nowrap;
}
.odt-delta-pill--ok   { background: #d4f4e5; color: #0d6e40; }
.odt-delta-pill--warn { background: #fde7c7; color: #8a5400; }
.odt-delta-pill--late { background: #fdd4d6; color: #931b1f; }
.odt-delta-sub { font-size: 9px; color: #9aa6b8; font-weight: 500; }

.odt-siguiente-molde { font-size: 10.5px; color: #657993; font-weight: 600; display: flex; align-items: center; gap: 3px; margin-top: 2px; }
.odt-no-sig          { color: #c8d7ea; font-size: 12px; }

.odt-estado-badges { display: flex; flex-wrap: wrap; gap: 3px; justify-content: center; }
.odt-badge {
  display: inline-flex; align-items: center;
  font-size: 10.5px; font-weight: 800; letter-spacing: 1px;
  padding: 4px 12px; border-radius: 999px; white-space: nowrap;
}
.odt-badge--green  { background: #d4f4e5; color: #0d6e40; }
.odt-badge--amber  { background: #fde7c7; color: #8a5400; }
.odt-badge--red    { background: #ffd6d6; color: #8a0000; }
.odt-badge--violet { background: #e8e2ff; color: #4527a0; }
.odt-badge--slate  { background: #e8edf6; color: #4a5e7a; }
.odt-badge--sm { font-size: 9px; padding: 2px 8px; }
.odt-empty { text-align: center; padding: 44px; color: #9aa6b8; font-size: .9rem; }

.odt-footer {
  display: flex; justify-content: space-between;
  padding: 14px 24px 0; font-size: 10.5px; color: #9aa6b8; font-weight: 600; letter-spacing: .8px;
}

@media (max-width: 1200px) { .odt-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 860px) {
  .odt-kpi-grid { grid-template-columns: repeat(2, 1fr); }
  .odt-cambio-grid { grid-template-columns: 1fr; }
  .odt-panel, .odt-section-label, .odt-cambio-grid { margin-left: 12px; margin-right: 12px; }
  .odt-refresh-timer { display: none; }
}
@media (max-width: 540px) {
  .odt-kpi-grid { grid-template-columns: 1fr 1fr; }
  .odt-search { width: 160px; }
  .odt-kpi-value-main { font-size: 50px !important; }
}
</style>
